import {
  Modal,
  SvgIcon,
  Button,
  tokens,
  Typography,
  SvgNames,
  Tabs,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";
import styled from "@emotion/styled/macro";
import { Proposal } from "cap/capTypes";
import {
  financialProductDisplayText,
  FLEX_MONTHLY,
} from "constants/financialProducts";
import { SolarProductionGraph } from "components/organisms/SolarProductionGraph";
import { TargetZoneGraph } from "components/organisms/TargetZoneGraph";
import {
  addToolTips,
  getProductionFromProposal,
  getBaselineProductionFromProposal,
} from "utils/graphUtils";

const getSolarProductionContent = (
  isFlexMonthly: boolean,
  proposal?: Proposal
): {
  barData: { month: string; value: number }[];
  lineData: { x: number; y: number; y0: 0 }[];
  secondaryLineData: { x: number; y: number; y0: 0 }[];
} => {
  let lineData: { x: number; y: number; y0: 0 }[];
  let secondaryLineData: { x: number; y: number; y0: 0 }[];

  if (isFlexMonthly) {
    secondaryLineData =
      getProductionFromProposal(proposal).map(
        (value: number, index: number) => ({
          x: index + 1,
          y: Number(value) || 0,
          y0: 0,
        })
      ) || [];

    lineData =
      getBaselineProductionFromProposal(proposal).map(
        (value: number, index: number) => ({
          x: index + 1,
          y0: 0,
          y: value,
        })
      ) || [];
  } else {
    lineData =
      proposal?.monthlySolarProduction
        ?.replace(/[{}]/g, "")
        .split(",")
        .map((value, index) => ({
          x: index + 1,
          y: Number(value) || 0,
          y0: 0,
        })) || [];
    secondaryLineData = [];
  }

  const barData = [
    { month: "Jan", value: proposal?.janUsage ?? 0 },
    { month: "Feb", value: proposal?.febUsage ?? 0 },
    { month: "Mar", value: proposal?.marUsage ?? 0 },
    { month: "Apr", value: proposal?.aprUsage ?? 0 },
    { month: "May", value: proposal?.mayUsage ?? 0 },
    { month: "Jun", value: proposal?.junUsage ?? 0 },
    { month: "Jul", value: proposal?.julUsage ?? 0 },
    { month: "Aug", value: proposal?.augUsage ?? 0 },
    { month: "Sep", value: proposal?.sepUsage ?? 0 },
    { month: "Oct", value: proposal?.octUsage ?? 0 },
    { month: "Nov", value: proposal?.novUsage ?? 0 },
    { month: "Dec", value: proposal?.decUsage ?? 0 },
  ];
  return {
    barData,
    lineData,
    secondaryLineData,
  };
};

const graphTabs = ["Solar production", "Target Zone"];

const ProductionModal = ({
  proposal,
  toggleModal,
}: {
  proposal: Proposal | undefined;
  toggleModal: () => void;
}) => {
  const isFlexMonthly = proposal?.dealType === "FlexMonthly";

  return (
    <StyledModal hideClose testId="production-modal">
      <Tabs
        tabs={graphTabs}
        contents={[
          <SolarProductionSection
            proposal={proposal}
            isFlexMonthly={isFlexMonthly}
          />,
          <TargetZoneSection
            proposal={proposal}
            isFlexMonthly={isFlexMonthly}
          />,
        ]}
        initialTabIndex={0}
        onSelectTab={() => {
          // default behavior of tabs
        }}
      />
      <CloseButton
        size="sm"
        onClick={toggleModal}
        iconMiddle={
          <SvgIcon
            name={SvgNames.Close}
            width={35}
            height={35}
            color={tokens.TINTS_OFF_WHITE_90}
          />
        }
      />
    </StyledModal>
  );
};

const CloseButton = styled(Button)`
  position: absolute;
  right: 24px;
  top: 24px;
`;

const TabSectionDescription = ({
  graphHeading,
  graphDescription,
}: {
  graphHeading: string | ReactNode;
  graphDescription: string | ReactNode;
}) => {
  return (
    <SolarTextContainer>
      <Typography
        size={tokens.FONT_SIZE_8}
        color={tokens.BRAND_HEROBLUE}
        lineHeight={53}
      >
        <GraphHeading>{graphHeading}</GraphHeading>
      </Typography>
      <br />
      <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
        {graphDescription}
      </Typography>
    </SolarTextContainer>
  );
};

const SolarProductionSection = ({
  isFlexMonthly,
  proposal,
}: {
  isFlexMonthly: boolean;
  proposal: Proposal | undefined;
}) => {
  const { barData, lineData, secondaryLineData } = getSolarProductionContent(
    isFlexMonthly,
    proposal
  );

  const barDataWithTooltips = addToolTips({
    barData,
    production: getProductionFromProposal(proposal),
    isFlexMonthly,
    baselineProduction: getBaselineProductionFromProposal(proposal),
  });

  const graphHeading = isFlexMonthly
    ? "You're unlocking energy for life"
    : "Your home, Your energy";

  const graphDescription = isFlexMonthly
    ? `Your new energy system is designed to create solar energy with the industry's best products. And with ${financialProductDisplayText[FLEX_MONTHLY]}, you are all set for your future increased electricity needs.`
    : "By generating your own affordable energy, you can power your home, take control over your energy bills, and increase energy independence.";

  const legendData = [
    {
      name: "Usage below solar production",
      symbol: { fill: tokens.BRAND_CHARGEBLUE, type: SvgNames.LegendBar },
      height: 15,
      width: 15,
      offset: { x: -5, y: -5 },
    },
    {
      name: "Solar production",
      symbol: { type: SvgNames.LegendLine },
      offset: { x: -1, y: -7 },
    },
    {
      name: "Usage above solar production",
      symbol: { fill: tokens.INFO_60, type: SvgNames.LegendBar },
      height: 15,
      width: 15,
      offset: { x: -5, y: -5 },
    },
    ...(isFlexMonthly
      ? [
          {
            name: `${financialProductDisplayText[FLEX_MONTHLY]} production`,
            symbol: { type: SvgNames.LegendDots },
            offset: { x: -1, y: -7 },
          },
        ]
      : []),
  ];

  return (
    <Container>
      <TabSectionDescription
        graphDescription={graphDescription}
        graphHeading={graphHeading}
      />
      <SolarCard>
        <SolarProductionGraph
          data={{
            barData: barDataWithTooltips,
            lineData,
            secondaryLineData,
            legendData,
            legendBreakPoint: 900,
          }}
          isFlexMonthly={isFlexMonthly}
        />
      </SolarCard>
    </Container>
  );
};

const TargetZoneSection = ({
  isFlexMonthly,
  proposal,
}: {
  isFlexMonthly: boolean;
  proposal: Proposal | undefined;
}) => {
  const { barData, lineData: rawData } = getSolarProductionContent(
    isFlexMonthly,
    proposal
  );

  const targetDifference =
    (rawData.reduce(
      (accumulator: number, { y }: { y: number }) => accumulator + y,
      0
    ) *
      0.2) /
    12;

  const areaData = rawData.map(({ x, y }) => ({
    x,
    y,
    y0: y - targetDifference,
  }));
  const lineData = rawData.map(({ x, y }) => ({
    x,
    y: y - targetDifference,
  }));

  const graphHeading = "Your solar production on target";

  const graphDescription = `It’s normal for solar production to vary from month to month due to
    changes in weather. The Target Zone in this graph shows the normal
    range of solar production for your system based on its design, local
    peer data, and seasonality of solar production.`;

  return (
    <Container>
      <TabSectionDescription
        graphDescription={graphDescription}
        graphHeading={graphHeading}
      />
      <SolarCard>
        <TargetZoneGraph
          data={{
            barData,
            lineData,
            areaData,
            legendOnTop: false,
          }}
        />
      </SolarCard>
    </Container>
  );
};

const StyledModal = styled(Modal)`
  .modal-container {
    width: 90%;

    @media (max-width: 1048px) {
      width: 100%;
    }
  }
`;

const Container = styled.div`
  display: flex;
  padding-top: 32px;

  @media (max-width: 1048px) {
    flex-direction: column;
  }
`;

const GraphHeading = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 1048px) {
    flex-direction: row;
  }
`;

const SolarTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  color: #1f2647;
  width: 263px;
  font-style: normal;

  @media (max-width: 1048px) {
    width: 100%;
    margin: 0 10px;
  }
`;

const SolarCard = styled.div`
  height: 485px;
  width: auto;
  flex: 1;
  color: white;
  border: 1px solid #ffffff;
  box-sizing: border-box;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  background: linear-gradient(180deg, #1f2647 0%, #000000 270.13%);
  appearance: none;
  text-align: left;
  margin-left: 35px;

  @media (max-width: 1048px) {
    margin-left: 0px;
    margin-top: 35px;
    flex: unset;
    height: 485px;
    width: auto;
  }
`;

export { ProductionModal };
